import { useEffect, useMemo, useState } from 'react'
import { explorerWalletUrl, walletLast4 } from '@/lib/launchpad/contracts'
import { holderRole, type HolderRole, type LaunchHolder } from '@/lib/launchpad/holders'
import { weiToEthString } from '@/lib/launchpad/wei'

const HOLDERS_PER_PAGE = 25

function formatPercent(value: number) {
  if (!Number.isFinite(value) || value <= 0)
    return '0%'
  if (value < 0.01)
    return '<0.01%'
  return `${value.toFixed(value >= 10 ? 1 : 2)}%`
}

function resolvedRole(holder: LaunchHolder): HolderRole {
  return holder.role || holderRole(holder.address, holder.isCreator ? holder.address : undefined)
}

function holderTitle(role: HolderRole, address: string) {
  if (role === 'creator')
    return 'Creator'
  if (role === 'token')
    return 'Token Contract'
  if (role === 'uniswap')
    return 'Uniswap'
  if (role === 'burn')
    return 'Burn'
  return `…${walletLast4(address)}`
}

function holderHint(role: HolderRole) {
  if (role === 'token')
    return 'Token contract — not a holder'
  if (role === 'uniswap')
    return 'Liquidity pool — not a holder'
  if (role === 'burn')
    return 'Dead address — not a holder'
  return undefined
}

function holderAriaLabel(role: HolderRole, address: string) {
  if (role === 'creator')
    return 'Creator wallet on rh-scan'
  if (role === 'token')
    return 'Token contract on rh-scan — not an actual holder'
  if (role === 'uniswap')
    return 'Uniswap liquidity pool on rh-scan — not an actual holder'
  if (role === 'burn')
    return 'Burn address on rh-scan — not an actual holder'
  return `Wallet ${walletLast4(address)} on rh-scan`
}

function rowClass(role: HolderRole) {
  if (role === 'creator')
    return 'rounded-2xl bg-lime-400/10 px-3'
  if (role === 'token')
    return 'rounded-2xl bg-amber-400/10 px-3'
  if (role === 'uniswap')
    return 'rounded-2xl bg-cyan-400/10 px-3'
  if (role === 'burn')
    return 'rounded-2xl bg-white/[0.04] px-3'
  return ''
}

function nameClass(role: HolderRole) {
  if (role === 'creator')
    return 'font-semibold text-lime-400'
  if (role === 'token')
    return 'font-semibold text-amber-300'
  if (role === 'uniswap')
    return 'font-semibold text-cyan-300'
  if (role === 'burn')
    return 'font-semibold text-zinc-400'
  return 'text-zinc-200'
}

function amountClass(role: HolderRole) {
  if (role === 'creator')
    return 'text-lime-400'
  if (role === 'token')
    return 'text-amber-300'
  if (role === 'uniswap')
    return 'text-cyan-300'
  if (role === 'burn')
    return 'text-zinc-400'
  return 'text-white'
}

function percentClass(role: HolderRole) {
  if (role === 'creator')
    return 'text-lime-400/80'
  if (role === 'token')
    return 'text-amber-300/80'
  if (role === 'uniswap')
    return 'text-cyan-300/80'
  if (role === 'burn')
    return 'text-zinc-500'
  return 'text-zinc-500'
}

function HoldersList({
  holders,
  loading,
  emptyLabel,
}: {
  holders: LaunchHolder[]
  loading: boolean
  emptyLabel: string
}) {
  const [page, setPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(holders.length / HOLDERS_PER_PAGE))

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages))
  }, [totalPages])

  const pageHolders = useMemo(() => {
    const start = (page - 1) * HOLDERS_PER_PAGE
    return holders.slice(start, start + HOLDERS_PER_PAGE)
  }, [holders, page])

  if (loading && holders.length === 0)
    return <p className="mt-4 text-sm text-zinc-500">Scanning holders…</p>
  if (holders.length === 0)
    return <p className="mt-4 text-sm text-zinc-500">{emptyLabel}</p>

  const start = (page - 1) * HOLDERS_PER_PAGE + 1
  const end = Math.min(page * HOLDERS_PER_PAGE, holders.length)

  return (
    <div>
      <ul className="mt-4 divide-y divide-white/10">
        {pageHolders.map((holder) => {
          const href = explorerWalletUrl(holder.address)
          const role = resolvedRole(holder)
          const hint = holderHint(role)
          return (
            <li
              key={holder.address.toLowerCase()}
              className={`flex items-center justify-between gap-3 py-3 ${rowClass(role)}`}
            >
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={holderAriaLabel(role, holder.address)}
                className={`rounded-sm text-sm underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-400 ${
                  role === 'wallet' ? 'font-mono' : ''
                } ${nameClass(role)}`}
              >
                <span className="block">{holderTitle(role, holder.address)}</span>
                {hint ? <span className="mt-0.5 block text-[11px] font-normal text-zinc-500">{hint}</span> : null}
              </a>
              <div className="text-right">
                <p className={`text-sm font-semibold ${amountClass(role)}`}>
                  {weiToEthString(holder.amount, 2)}
                </p>
                <p className={`text-xs ${percentClass(role)}`}>
                  {formatPercent(holder.percent)}
                </p>
              </div>
            </li>
          )
        })}
      </ul>
      {holders.length > HOLDERS_PER_PAGE ? (
        <nav
          className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4"
          aria-label="Holders pagination"
        >
          <p className="text-xs text-zinc-500">
            {start}–{end} of {holders.length}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
              className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white transition hover:border-lime-400/50 hover:text-lime-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/15 disabled:hover:text-white"
            >
              Previous
            </button>
            <p className="min-w-[4.5rem] text-center text-xs text-zinc-400">
              {page} / {totalPages}
            </p>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={page >= totalPages}
              className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white transition hover:border-lime-400/50 hover:text-lime-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/15 disabled:hover:text-white"
            >
              Next
            </button>
          </div>
        </nav>
      ) : null}
    </div>
  )
}

export default HoldersList
